// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The Quest Browser session recycle, and the headset coming off (M5-28, M5-29, XR-030).
//
// Chess VR discovered — and documented at length — that Quest Browser on affected Horizon builds
// can lose its browser activity after several minutes in immersive WebXR while the operating
// system itself stays healthy. Its mitigation was a clean recycle of the WebXR session after 165
// seconds, with the page and the game left mounted. 06 §7 inherits that mitigation, with the same
// build-time controls, "because there is no evidence the platform issue is fixed and discovering
// it in production a second time would be inexcusable".
//
// **Only the session ends.** The page, the game, the ball in play and any online connection stay
// live. That is possible at all because the simulation state is a plain serialisable structure in
// the core rather than scene-graph state (03 §2), so "pause, drop the session, get a new one,
// resume" touches nothing the game knows about. A ball in flight resumes exactly where it stopped
// — which is M5-28's done-when, and is a property of the architecture rather than of this file.
//
// **The warning follows the camera and it is not a modal.** Fifteen seconds is long enough to
// finish a shot and not long enough to be an interruption, so it goes where the player is already
// looking and never takes the controls.
//
// **Headset removal is a different event with the same answer.** `XRSession.visibilityState` goes
// `hidden` when the headset is off the head or the system menu is up. The game pauses; putting it
// back on resumes it. The reason it shares a file with the recycle is that both are "the session
// is about to stop being usable and the game must not notice" — and keeping one place that owns
// that means there is one place to be careful about the resume.
//
// **Off in the product unless a build turns it on** (13 Sep 2026, `safetyFlag.ts`). The class keeps
// its own default — on, at 165 — because that is the mitigation and the tests drive it; what the
// product passes in is the build's setting, and the headset this was measured on (Horizon OS v2.7,
// Browser 150.1) no longer launches the task the recycle was written to stay ahead of.

/** Seconds of immersive presence before a session is recycled (XR-030). */
export const RECYCLE_AFTER_SECONDS = 165

/** How long the warning shows before it happens. */
export const WARN_FOR_SECONDS = 15

/** What the watchdog wants the shell to do this frame. */
export type SafetyAction =
  | { readonly kind: 'none' }
  /** Show the camera-following countdown. `seconds` is what it should read. */
  | { readonly kind: 'warn'; readonly seconds: number }
  /** Recycle now: pause, end the session, offer to go back in. */
  | { readonly kind: 'recycle' }
  /** The headset came off or the system menu took over. Pause; do not end anything. */
  | { readonly kind: 'pause' }
  /** It came back. Resume. */
  | { readonly kind: 'resume' }

export interface SessionSafetyOptions {
  /**
   * Whether the recycle runs at all.
   *
   * 06 §7: "configurable and can be disabled once a Horizon build passes the no-growth
   * qualification gate". Defaulting it **on** is the whole point — a mitigation that has to be
   * switched on is a mitigation that will be off in production the day it is needed.
   */
  readonly recycle?: boolean
  readonly afterSeconds?: number
  readonly warnSeconds?: number
}

/**
 * The immersive-session watchdog.
 *
 * Driven from the frame loop with the elapsed time and the session's visibility. It holds no
 * timers of its own — a `setTimeout` would keep running while the session was hidden and would
 * recycle a session the player was not even looking at.
 */
export class SessionSafety {
  private readonly enabled: boolean
  private readonly after: number
  private readonly warn: number

  private elapsed = 0
  private warned = false
  private recycled = false
  private hidden = false

  constructor(options: SessionSafetyOptions = {}) {
    this.enabled = options.recycle !== false
    this.after = options.afterSeconds ?? RECYCLE_AFTER_SECONDS
    this.warn = options.warnSeconds ?? WARN_FOR_SECONDS
  }

  /** Seconds of visible presence so far. */
  get presentedFor(): number {
    return this.elapsed
  }

  /** Start the clock again after a recycle, without rebuilding anything. */
  restart(): void {
    this.elapsed = 0
    this.warned = false
    this.recycled = false
  }

  /**
   * One frame.
   *
   * `visible` is `session.visibilityState === 'visible'`. Time **does not accumulate while
   * hidden**: a headset resting on a desk for ten minutes has not been in an immersive session
   * for ten minutes, and recycling on the strength of it would drop a session the moment the
   * player picked the headset back up.
   */
  advance(frameSeconds: number, visible: boolean): SafetyAction {
    if (!visible) {
      if (this.hidden) return { kind: 'none' }
      this.hidden = true
      return { kind: 'pause' }
    }
    if (this.hidden) {
      this.hidden = false
      return { kind: 'resume' }
    }

    if (!this.enabled || this.recycled) return { kind: 'none' }

    this.elapsed += frameSeconds
    const left = this.after - this.elapsed
    if (left <= 0) {
      this.recycled = true
      return { kind: 'recycle' }
    }
    if (left <= this.warn) {
      this.warned = true
      // Ceiling, so a countdown reads "15" for the whole first second rather than flashing 14.
      return { kind: 'warn', seconds: Math.ceil(left) }
    }
    return { kind: 'none' }
  }

  /** Has the warning been shown for this cycle? For a shell deciding whether to animate it in. */
  get warning(): boolean {
    return this.warned
  }
}

/**
 * The sentence the countdown shows.
 *
 * Written to say what will and will not happen, in that order, because the thing a player needs
 * to know is that their ball is safe. "Reconnecting" rather than "restarting": the game is not
 * restarting and the word would be alarming.
 */
export function recycleWarning(seconds: number): string {
  return `Reconnecting the headset display in ${seconds}s — your game keeps playing.`
}
